import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import { mediaBlobUrl, removeMedia, useMediaVersion, type MediaEntry } from './media';
import './mediaManager.css';

type Learner = { id: string; name: string };
type Removal = { entry: MediaEntry; kind: 'image' | 'audio' };

const TALK = 'talk:';

function Thumb({ id }: { id: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void mediaBlobUrl(id).then((u) => { if (live) setUrl(u ?? null); });
    return () => { live = false; };
  }, [id]);
  return url ? <img src={url} alt="" className="mm-thumb" /> : <span className="mm-thumb" aria-hidden="true" />;
}

export default function MediaManager() {
  const t = useT();
  const version = useMediaVersion();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<MediaEntry[] | null>(null);
  const [learners, setLearners] = useState<Learner[]>([]);
  const [removing, setRemoving] = useState<Removal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);

  const load = useCallback(async () => {
    try {
      const [all, people] = await Promise.all([
        api<MediaEntry[]>('/media/all', { token: true }),
        api<Learner[]>('/learners', { token: true }).catch(() => [] as Learner[]),
      ]);
      setEntries(all);
      setLearners(people);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not load your photos and recordings.'));
    }
  }, [t]);

  useEffect(() => { void load(); }, [load, version]);
  useEffect(() => () => audio.current?.pause(), []);

  if (!entries || entries.length === 0) return null;

  const whoFor = (id: string | null) => (id ? learners.find((l) => l.id === id)?.name ?? t('One learner') : t('Every learner'));
  const wordOf = (term: string) => (term.startsWith(TALK) ? term.slice(TALK.length) : term);

  async function listen(id: string) {
    const url = await mediaBlobUrl(id);
    if (!url) return;
    audio.current?.pause();
    audio.current = new Audio(url);
    void audio.current.play().catch(() => {});
  }

  async function remove(r: Removal) {
    setError(null);
    try {
      await removeMedia(r.kind, r.entry.term, r.entry.learnerId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be removed just now.'));
    }
  }

  return (
    <div className="mm">
      <button type="button" className="btn secondary" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? t('Hide your photos and recordings') : t('Manage your photos and recordings ({n})', { n: entries.length })}
      </button>
      {error && <div className="error" role="alert">{error}</div>}
      {open && (
        <ul className="list mm-list">
          {entries.map((e) => (
            <li key={`${e.term}|${e.learnerId ?? ''}`} className="list-item mm-item">
              {e.imageId ? <Thumb id={e.imageId} /> : <span className="mm-thumb mm-thumb-empty" aria-hidden="true">🔊</span>}
              <div className="list-item-text">
                <div className="name">{wordOf(e.term)}</div>
                <div className="muted">
                  {e.term.startsWith(TALK) ? t('Talk Board') : t('Games')} · {whoFor(e.learnerId)}
                </div>
              </div>
              <div className="toolbar mm-actions">
                {e.audioId && <button type="button" className="btn ghost" onClick={() => void listen(e.audioId as string)}>{t('Listen')}</button>}
                {e.imageId && <button type="button" className="btn quiet-danger" onClick={() => setRemoving({ entry: e, kind: 'image' })}>{t('Remove photo')}</button>}
                {e.audioId && <button type="button" className="btn quiet-danger" onClick={() => setRemoving({ entry: e, kind: 'audio' })}>{t('Remove recording')}</button>}
              </div>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={removing !== null}
        title={removing?.kind === 'image' ? t('Remove this photo?') : t('Remove this recording?')}
        message={t('It is deleted straight away and cannot be brought back. "{word}" goes back to the built-in picture and voice.', { word: removing ? wordOf(removing.entry.term) : '' })}
        confirmLabel={removing?.kind === 'image' ? t('Remove photo') : t('Remove recording')}
        destructive
        onConfirm={() => { const r = removing; setRemoving(null); if (r) void remove(r); }}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
